import type maplibregl from 'maplibre-gl';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import AircraftPanel from './components/AircraftPanel';
import type { LiveUpdate } from './components/MapView';
import SearchPanel from './components/SearchPanel';
import TrackReplay from './components/TrackReplay';
import WatchlistPanel from './components/WatchlistPanel';
import type { MapStyleMode } from './components/MapView';
import {
  api,
  type Aircraft,
  type AircraftMeta,
  type FlightTrack,
  type LiveResponse,
  type RouteDetail,
} from './lib/api';
import { useWatchlist } from './lib/useWatchlist';

// MapLibre (~1 MB) erst nach dem First Paint laden — UI-Shell rendert sofort.
const MapView = lazy(() => import('./components/MapView'));

const STYLE_KEY = 'adsb-tracker.mapstyle';
const MOBILE_Q = '(max-width: 640px)';

function isMobile(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_Q).matches;
}

/**
 * Auf einen Flieger zentrieren und ihn aus dem überlagerten Bereich schieben:
 * Mobile → nach oben (über dem Bottom-Sheet-Peek), Desktop → nach links
 * (neben dem rechten Detailpanel), damit der getrackte Flieger sichtbar bleibt.
 */
function focusOnMap(map: maplibregl.Map, lon: number, lat: number) {
  map.flyTo({
    center: [lon, lat],
    zoom: Math.max(map.getZoom(), 7),
    offset: isMobile() ? [0, -Math.round(window.innerHeight * 0.2)] : [-150, 0],
    duration: 700,
  });
}

export default function App() {
  const [selected, setSelected] = useState<Aircraft | null>(null);
  const [meta, setMeta] = useState<AircraftMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [live, setLive] = useState<LiveUpdate | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [replay, setReplay] = useState<FlightTrack | null>(null);
  const [map, setMap] = useState<maplibregl.Map | null>(null);
  const [styleMode, setStyleMode] = useState<MapStyleMode>(() =>
    localStorage.getItem(STYLE_KEY) === 'light' ? 'light' : 'dark',
  );
  const [route, setRoute] = useState<RouteDetail | null>(null);
  const [trailSeed, setTrailSeed] = useState<[number, number][] | null>(null);
  // Beim Replay schrumpft das Detailpanel auf die Kopfzeile (v. a. Mobile/iOS)
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const watchlist = useWatchlist();

  const selectedHex = selected?.hex ?? null;
  const selectedCallsign = selected?.flight ?? null;

  const toggleStyle = useCallback(() => {
    setStyleMode((cur) => {
      const next = cur === 'dark' ? 'light' : 'dark';
      localStorage.setItem(STYLE_KEY, next);
      return next;
    });
  }, []);

  // Route (Origin/Destination) zum Callsign — für die Linien auf der Karte
  useEffect(() => {
    if (!selectedCallsign) {
      setRoute(null);
      return;
    }
    const ctrl = new AbortController();
    api
      .route(selectedCallsign, ctrl.signal)
      .then(setRoute)
      .catch(() => setRoute(null));
    return () => ctrl.abort();
  }, [selectedCallsign]);

  // Bisheriger Flugweg als Trail-Basis: zuerst der exakte Track aus dem
  // adsb.lol-Tagesarchiv (funktioniert für jeden Flieger), als Fallback der
  // gespeicherte Track des offenen Watchlist-Flugs.
  useEffect(() => {
    setTrailSeed(null);
    if (!selectedHex) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const day = await api.archive(selectedHex, today, ctrl.signal);
        const seg = day.segments[day.segments.length - 1];
        // Nur übernehmen, wenn das Segment der laufende Flug ist (Ende < 20 min her)
        if (seg && Date.now() / 1000 - seg.end_ts < 20 * 60 && seg.points.length >= 2) {
          setTrailSeed(seg.points.map((p) => [p.lon, p.lat]));
          return;
        }
      } catch {
        // Archiv nicht verfügbar (z. B. OGN-IDs) — Fallback unten
      }
      try {
        const flights = await api.flights(selectedHex, ctrl.signal);
        const open = flights.find((f) => f.status === 'open');
        if (!open) return;
        const track = await api.track(open.id, ctrl.signal);
        setTrailSeed(track.points.map((p) => [p.lon, p.lat]));
      } catch {
        // keine Trail-Basis — Live-Trail wächst ab jetzt
      }
    })();
    return () => ctrl.abort();
  }, [selectedHex]);

  // Stammdaten beim Auswählen nachladen (Worker cacht in D1)
  useEffect(() => {
    if (!selectedHex) {
      setMeta(null);
      return;
    }
    const ctrl = new AbortController();
    setMetaLoading(true);
    setMeta(null);
    api
      .meta(selectedHex, ctrl.signal)
      .then(setMeta)
      .catch(() => setMeta(null))
      .finally(() => setMetaLoading(false));
    return () => ctrl.abort();
  }, [selectedHex]);

  // Panel-Telemetrie aus dem laufenden Polling aktuell halten
  const handleLive = useCallback((update: LiveUpdate) => {
    setLive(update);
    setSelected((cur) =>
      cur ? (update.aircraft.find((a) => a.hex === cur.hex) ?? cur) : cur,
    );
  }, []);

  const handleSelect = useCallback((ac: Aircraft | null) => {
    setSelected(ac);
    // Mobile: Peek-zuerst (nur Live-Streifen), damit die Karte sichtbar bleibt.
    // Desktop: volles Panel rechts (verdeckt die Karte nicht).
    setPanelCollapsed(ac ? isMobile() : false);
    const map = mapRef.current;
    if (ac && map && ac.lon != null && ac.lat != null) focusOnMap(map, ac.lon, ac.lat);
  }, []);
  const handleMapReady = useCallback((m: maplibregl.Map) => {
    mapRef.current = m;
    setMap(m);
  }, []);

  const handleReplay = useCallback(async (flightId: number) => {
    try {
      setReplay(await api.track(flightId));
      setPanelCollapsed(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const handleReplayTrack = useCallback((track: FlightTrack) => {
    setReplay(track);
    setPanelCollapsed(true);
  }, []);

  const handleReplayClose = useCallback(() => {
    setReplay(null);
    setPanelCollapsed(false);
  }, []);

  const focusAircraft = useCallback((ac: Aircraft) => {
    const map = mapRef.current;
    if (map && ac.lat != null && ac.lon != null) focusOnMap(map, ac.lon, ac.lat);
  }, []);

  const toggleWatch = useCallback(async () => {
    if (!selected) return;
    const label = selected.r ?? selected.flight ?? null;
    const err = watchlist.watched.has(selected.hex)
      ? await watchlist.remove(selected.hex)
      : await watchlist.add(selected.hex, label);
    if (err) alert(err);
  }, [selected, watchlist]);

  const handleSearch = useCallback(async (query: string): Promise<string | null> => {
    const q = query.toLowerCase().trim();
    // Reihenfolge nach Wahrscheinlichkeit: 6 Hex-Zeichen → Hex,
    // mit Bindestrich → Registrierung, sonst Callsign zuerst.
    const attempts: Array<() => Promise<LiveResponse>> = [];
    if (/^[0-9a-f]{6}$/.test(q)) attempts.push(() => api.hex([q]));
    if (q.includes('-')) {
      attempts.push(() => api.reg(q), () => api.callsign(q));
    } else {
      attempts.push(() => api.callsign(q), () => api.reg(q));
    }

    for (const attempt of attempts) {
      const res = await attempt().catch(() => null);
      const ac = res?.ac.find((a) => a.lat != null) ?? res?.ac[0];
      if (ac) {
        setSelected(ac);
        setPanelCollapsed(isMobile());
        const map = mapRef.current;
        if (map && ac.lat != null && ac.lon != null) focusOnMap(map, ac.lon, ac.lat);
        return null;
      }
    }
    // Kein Live-Treffer: Kennung über die Stammdaten-Quellen auflösen —
    // Panel mit Historie öffnet auch, wenn der Flieger am Boden steht.
    try {
      const found = await api.find(q);
      setSelected({ hex: found.hex, r: found.meta?.registration ?? undefined });
      setPanelCollapsed(isMobile());
      return null;
    } catch {
      return 'Nichts gefunden — weder live noch in den Stammdaten-Quellen.';
    }
  }, []);

  return (
    <div className="app">
      <Suspense fallback={<div className="map map-loading mono">RADAR WIRD INITIALISIERT …</div>}>
        <MapView
          selectedHex={selectedHex}
          styleMode={styleMode}
          trailSeed={trailSeed}
          route={route}
          onSelect={handleSelect}
          onLive={handleLive}
          onPollError={setPollError}
          onMapReady={handleMapReady}
        />
      </Suspense>

      <header className="topbar">
        <h1>
          ADS-B <span className="accent">TRACKER</span>
        </h1>
        <SearchPanel onSearch={handleSearch} />
        <button className="style-toggle" onClick={toggleStyle}>
          {styleMode === 'dark' ? '☀ Helle Karte' : '☾ Dunkle Karte'}
        </button>
        <WatchlistPanel
          entries={watchlist.entries}
          selectedHex={selectedHex}
          onSelect={handleSelect}
          onRemove={watchlist.remove}
          onFocus={focusAircraft}
          token={watchlist.token}
          setToken={watchlist.setToken}
        />
      </header>

      {selected && !panelCollapsed && (
        <div
          className="sheet-scrim"
          onClick={() => setPanelCollapsed(true)}
          aria-hidden="true"
        />
      )}

      {selected && (
        <AircraftPanel
          aircraft={selected}
          meta={meta}
          metaLoading={metaLoading}
          route={route}
          onClose={() => setSelected(null)}
          watched={watchlist.watched.has(selected.hex)}
          onToggleWatch={toggleWatch}
          onReplay={handleReplay}
          onReplayTrack={handleReplayTrack}
          collapsed={panelCollapsed}
          replayActive={!!replay}
          onToggleCollapse={() => setPanelCollapsed((c) => !c)}
          onExpand={() => setPanelCollapsed(false)}
          onCollapse={() => setPanelCollapsed(true)}
        />
      )}

      {replay && map && <TrackReplay map={map} track={replay} onClose={handleReplayClose} />}

      <div className={`statusbar ${pollError ? 'statusbar-error' : ''}`}>
        <span className="mono">
          {pollError
            ? 'Quellen nicht erreichbar — neuer Versuch in 6 s'
            : live
              ? `${live.aircraft.length} Flugzeuge · ${live.source} · ${new Date(
                  live.at,
                ).toLocaleTimeString('de-DE')}${live.zoom < 4.5 ? ' · Radius 250 NM — zoom für mehr' : ''}`
              : 'Verbinde …'}
        </span>
      </div>

      <footer className="data-attribution">
        Flugdaten:{' '}
        <a href="https://adsb.lol" target="_blank" rel="noreferrer">
          adsb.lol
        </a>
        ,{' '}
        <a href="https://airplanes.live" target="_blank" rel="noreferrer">
          airplanes.live
        </a>
        ,{' '}
        <a href="https://adsb.fi" target="_blank" rel="noreferrer">
          adsb.fi
        </a>{' '}
        (
        <a href="https://opendatacommons.org/licenses/odbl/" target="_blank" rel="noreferrer">
          ODbL
        </a>
        ) · Stammdaten:{' '}
        <a href="https://adsbdb.com" target="_blank" rel="noreferrer">
          adsbdb
        </a>
      </footer>
    </div>
  );
}
